import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Input,
    Button,
    Form,
    FormGroup,
    Label,
    FormFeedback,
    Spinner
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// API calls
import { createUser, createManualSubscription, getRoutesSlotStoreByPincode } from "../User/_requests";
import { listSubscriptionPlans } from "../Plan-Subscription/_requests";

const AddSubscription = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [plans, setPlans] = useState<any[]>([]);
    const [pincodeData, setPincodeData] = useState<any>(null);
    const [selectedPlanDetails, setSelectedPlanDetails] = useState<any>(null);

    const [formData, setFormData] = useState({
        name: "",
        mobile_number: "",
        shipping_address: "",
        postal_code: "",
        city: "",

        subscription_id: "",
        type: 1,
        is_trial_plan: false,
        amount: "0",
        slot_type: 1,
        store_id: "",
        route_id: "",
        description: "",
        payment_type: "1",
        reference_payment_id: "",
        details: [] as any[]
    });

    useEffect(() => {
        // Fetch all subscription plans
        listSubscriptionPlans()
            .then((res: any) => {
                if (res.data) setPlans(res.data);
            })
            .catch((err) => console.error("Error fetching plans:", err));
    }, []);

    const handleInputChange = (e: any) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePincodeBlur = async () => {
        if (formData.postal_code && formData.postal_code.length >= 6) {
            try {
                const response: any = await getRoutesSlotStoreByPincode({ pincode: formData.postal_code });
                setPincodeData(response.data);
                setFormData((prev) => ({
                    ...prev,
                    route_id: response.data?.route_id?.toString() || "",
                    store_id: response.data?.store_id?.toString() || "",
                    slot_type: response.data?.slot_type ? Number(response.data.slot_type.split(',')[0]) : 1
                }));
            } catch (error) {
                toast.error("Failed to fetch delivery information for this pincode");
            }
        }
    };

    const handlePlanChange = (e: any) => {
        const planId = e.target.value;
        const plan = plans.find((p: any) => p.id.toString() === planId);

        if (plan) {
            setSelectedPlanDetails(plan);
            setFormData((prev) => ({
                ...prev,
                subscription_id: plan.id.toString(),
                type: plan.type || 1,
                is_trial_plan: plan.is_trial_plan || false,
                amount: plan.total_gross_amount?.toString() || "0",
                details: (plan.details || []).map((detail: any) => ({
                    product_id: detail.product_id?.toString() || "",
                    variant_id: detail.variant_id?.toString() || "",
                    variant_value: detail.variant_value?.toString() || "",
                    validity: detail.validity?.toString() || "",
                    validity_type: detail.validity_type || ""
                }))
            }));
        } else {
            setSelectedPlanDetails(null);
            setFormData((prev) => ({
                ...prev,
                subscription_id: "",
                details: []
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Step 1: Create User
            const userPayload = {
                name: formData.name,
                first_name: formData.name, // in case backend expects first_name
                email: `${formData.mobile_number}@example.com`, // dummy email if required
                mobile_number: formData.mobile_number,
                password: formData.mobile_number, // Default password
                state: "Tamil Nadu", // Default state
                city: formData.city || "Unknown",
                postal_code: formData.postal_code,
                address: formData.shipping_address,
            };

            const userRes: any = await createUser(userPayload);
            console.log("userRes===========", userRes)

            if (userRes?.message === "User already exists") {
                toast.error("This mobile number is already registered to a user.");
                setLoading(false);
                return;
            }

            // Get the injected user ID. 
            // Depending on actual backend response format, could be userRes.id, userRes.data.id, etc.
            const userId = userRes?.data?.id || userRes?.id || userRes?.user_id || userRes?.user?.id;
            const addressBookId = userRes?.data?.addressbook_id || userRes?.addressbook_id || "0";

            if (!userId) {
                toast.error("User created but failed to retrieve User ID.");
                setLoading(false);
                return;
            }

            // Step 2: Create Manual Subscription
            const subPayload = {
                user_id: userId,
                subscription_id: formData.subscription_id,
                type: formData.type,
                addressbook_id: addressBookId,
                is_trial_plan: formData.is_trial_plan,
                shipping_address: formData.shipping_address,
                amount: formData.amount,
                slot_type: formData.slot_type,
                store_id: formData.store_id,
                route_id: formData.route_id,
                description: formData.description || "Manual Subscription",
                payment_type: formData.payment_type,
                reference_payment_id: formData.reference_payment_id,
                details: formData.details.map(detail => ({
                    ...detail,
                    product_id: Number(detail.product_id),
                    variant_id: Number(detail.variant_id),
                    variant_value: Number(detail.variant_value),
                    validity: Number(detail.validity)
                }))
            };

            const subRes: any = await createManualSubscription(subPayload);
            toast.success(subRes?.data?.message || "Subscription activated successfully!");

            setTimeout(() => {
                navigate("/subscription");
            }, 1500);

        } catch (error: any) {
            toast.error(error?.response?.data?.message || "An error occurred while creating subscription.");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    document.title = "Paal Dappa | Admin panel - Add Subscription";

    return (
        <React.Fragment>
            <div className="page-content">
                <ToastContainer closeButton={false} limit={1} />
                <Container fluid>
                    <BreadCrumb title="Add Subscription" pageTitle="Subscription" />
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader>
                                    <h5 className="card-title mb-0">Create Manual Subscription</h5>
                                </CardHeader>
                                <CardBody>
                                    <Form onSubmit={handleSubmit}>
                                        <Row className="g-4">
                                            {/* Section 1: User Details */}
                                            <Col md={12}>
                                                <h6 className="fw-semibold text-primary mb-3">User Details</h6>
                                                <Row>
                                                    <Col md={6}>
                                                        <FormGroup>
                                                            <Label>Full Name <span className="text-danger">*</span></Label>
                                                            <Input
                                                                type="text"
                                                                name="name"
                                                                value={formData.name}
                                                                onChange={handleInputChange}
                                                                required
                                                                placeholder="Enter user's name"
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={6}>
                                                        <FormGroup>
                                                            <Label>Mobile Number <span className="text-danger">*</span></Label>
                                                            <Input
                                                                type="text"
                                                                name="mobile_number"
                                                                value={formData.mobile_number}
                                                                onChange={handleInputChange}
                                                                required
                                                                placeholder="Enter 10 digit mobile number"
                                                                maxLength={10}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={6}>
                                                        <FormGroup>
                                                            <Label>Pincode <span className="text-danger">*</span></Label>
                                                            <Input
                                                                type="text"
                                                                name="postal_code"
                                                                value={formData.postal_code}
                                                                onChange={handleInputChange}
                                                                onBlur={handlePincodeBlur}
                                                                required
                                                                placeholder="Enter pincode to fetch routes"
                                                                maxLength={6}
                                                            />
                                                            <small className="text-muted">Enter pincode and click out to fetch routes.</small>
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={6}>
                                                        <FormGroup>
                                                            <Label>City <span className="text-danger">*</span></Label>
                                                            <Input
                                                                type="text"
                                                                name="city"
                                                                value={formData.city}
                                                                onChange={handleInputChange}
                                                                required
                                                                placeholder="Enter city"
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={12}>
                                                        <FormGroup>
                                                            <Label>Shipping Address <span className="text-danger">*</span></Label>
                                                            <Input
                                                                type="text"
                                                                name="shipping_address"
                                                                value={formData.shipping_address}
                                                                onChange={handleInputChange}
                                                                required
                                                                placeholder="Enter complete delivery address"
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </Row>
                                            </Col>

                                            {/* Section 2: Plan & Delivery Details */}
                                            <Col md={12}>
                                                <h6 className="fw-semibold text-primary mb-3">Plan & Delivery Information</h6>
                                                <Row>
                                                    <Col md={12}>
                                                        <FormGroup>
                                                            <Label>Select Plan <span className="text-danger">*</span></Label>
                                                            <Input
                                                                type="select"
                                                                name="subscription_id"
                                                                value={formData.subscription_id}
                                                                onChange={handlePlanChange}
                                                                required
                                                            >
                                                                <option value="">Select a Plan</option>
                                                                {plans.map((plan: any) => (
                                                                    <option key={plan.id} value={plan.id}>
                                                                        {plan.name} - ₹{plan.total_gross_amount || 0}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={6}>
                                                        <FormGroup>
                                                            <Label>Route</Label>
                                                            <Input
                                                                type="text"
                                                                value={pincodeData?.route_name || "Route will be fetched automatically"}
                                                                disabled
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={6}>
                                                        <FormGroup>
                                                            <Label>Delivery Slot <span className="text-danger">*</span></Label>
                                                            <Input
                                                                type="select"
                                                                name="slot_type"
                                                                value={formData.slot_type}
                                                                onChange={handleInputChange}
                                                                required
                                                                disabled={!pincodeData?.slot_type}
                                                            >
                                                                {pincodeData?.slot_type ? (
                                                                    pincodeData.slot_type.split(',').map((slot: string) => (
                                                                        <option key={slot} value={slot}>
                                                                            {slot === '1' ? 'Morning (7AM - 11AM)' : 'Evening (4PM - 8PM)'}
                                                                        </option>
                                                                    ))
                                                                ) : (
                                                                    <option value="">Fetch Pincode First</option>
                                                                )}
                                                            </Input>
                                                        </FormGroup>
                                                    </Col>
                                                </Row>
                                            </Col>

                                            {/* Section 3: Payment details */}
                                            <Col md={12}>
                                                <h6 className="fw-semibold text-primary mb-3">Payment Details</h6>
                                                <Row>
                                                    <Col md={6}>
                                                        <FormGroup>
                                                            <Label>Total Amount</Label>
                                                            <Input type="text" value={`₹${formData.amount}`} disabled />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={6}>
                                                        <FormGroup>
                                                            <Label>Payment Type <span className="text-danger">*</span></Label>
                                                            <Input
                                                                type="select"
                                                                name="payment_type"
                                                                value={formData.payment_type}
                                                                onChange={handleInputChange}
                                                                required
                                                            >
                                                                <option value="1">Online</option>
                                                                <option value="2">Cash in Hand</option>
                                                            </Input>
                                                        </FormGroup>
                                                    </Col>
                                                    {formData.payment_type === "1" && (
                                                        <Col md={12}>
                                                            <FormGroup>
                                                                <Label>Reference Payment ID <span className="text-danger">*</span></Label>
                                                                <Input
                                                                    type="text"
                                                                    name="reference_payment_id"
                                                                    value={formData.reference_payment_id}
                                                                    onChange={handleInputChange}
                                                                    required
                                                                    placeholder="Enter payment reference"
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                    )}
                                                    <Col md={12}>
                                                        <FormGroup>
                                                            <Label>Description (Optional)</Label>
                                                            <Input
                                                                type="textarea"
                                                                name="description"
                                                                value={formData.description}
                                                                onChange={handleInputChange}
                                                                placeholder="Any additional notes"
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </Row>
                                            </Col>

                                        </Row>
                                        <div className="d-flex justify-content-end mt-4 border-top pt-3">
                                            <Button color="light" onClick={() => navigate("/subscription")} className="me-2" disabled={loading}>
                                                Cancel
                                            </Button>
                                            <Button color="primary" type="submit" disabled={loading || !formData.subscription_id || !formData.route_id}>
                                                {loading ? <Spinner size="sm" className="me-2" /> : null}
                                                Create Subscription
                                            </Button>
                                        </div>
                                    </Form>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default AddSubscription;
